import { useId, useState } from "react";
import type { FileNode } from "../../api.ts";
import { messageOf, useRename } from "../../lib/queries.ts";
import { Icon } from "../Icon.tsx";
import ui from "../ui.module.css";
import styles from "./FilePage.module.css";

export function RenameForm({ node, onDone }: { node: FileNode; onDone: () => void }) {
  const rename = useRename(node.id);
  const [name, setName] = useState(node.name);
  const inputId = useId();
  const trimmed = name.trim();
  const slash = trimmed.includes("/");
  const error = rename.error ? messageOf(rename.error) : null;

  return (
    <form
      className={styles.renameForm}
      onSubmit={(event) => {
        event.preventDefault();
        if (trimmed === "" || slash) return;
        if (trimmed === node.name) return onDone();
        rename.mutate({ name: trimmed }, { onSuccess: onDone });
      }}
    >
      <label className={ui.visuallyHidden} htmlFor={inputId}>
        {node.kind === "folder" ? "Folder name" : "File name"}
      </label>
      <input
        id={inputId}
        className={`${ui.input} ${styles.renameInput}`}
        value={name}
        maxLength={255}
        required
        spellCheck={false}
        autoComplete="off"
        // biome-ignore lint/a11y/noAutofocus: the field replaces the title the user just chose to edit
        autoFocus
        aria-invalid={error || slash ? true : undefined}
        onChange={(event) => setName(event.currentTarget.value)}
        onFocus={(event) => {
          const dot = node.kind === "file" ? event.currentTarget.value.lastIndexOf(".") : -1;
          event.currentTarget.setSelectionRange(
            0,
            dot > 0 ? dot : event.currentTarget.value.length,
          );
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") onDone();
        }}
      />
      <div className={styles.renameActions}>
        <button
          type="button"
          className={ui.button}
          data-variant="frame"
          onClick={onDone}
          disabled={rename.isPending}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={ui.button}
          data-variant="primary"
          disabled={rename.isPending || trimmed === "" || slash}
        >
          {rename.isPending ? "Saving…" : "Save"}
        </button>
      </div>
      {error || slash ? (
        <p className={styles.frameError} role="alert">
          <Icon name="alert" />
          {slash ? "A name cannot contain “/”." : error}
        </p>
      ) : null}
    </form>
  );
}
